import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import moment from "moment";
import React, { useMemo } from "react";
import { useIntl } from "react-intl";
import { Button } from "reactstrap";
import {
  MessageSchedulingChannel,
  ScheduledMessage,
  linkHostname,
} from "../../utils/messageSchedulingStorage";

const CHANNEL_ICON: Record<MessageSchedulingChannel, string> = {
  whatsapp: "whatsapp",
  facebook: "facebook",
  instagram: "instagram",
};

interface Props {
  items: ScheduledMessage[];
  onCreate: () => void;
  onEdit: (item: ScheduledMessage) => void;
  onDuplicate: (item: ScheduledMessage) => void;
  onDelete: (id: string) => void;
}

export const ScheduledMessageList = (props: Props) => {
  const { items, onCreate, onEdit, onDuplicate, onDelete } = props;
  const intl = useIntl();

  const stats = useMemo(() => {
    const upcoming = items.filter((item) =>
      moment(item.scheduledAt).isAfter(moment())
    );
    const week = upcoming.filter((item) =>
      moment(item.scheduledAt).isBefore(moment().add(7, "days"))
    );
    const recipients = new Set<string>();
    items.forEach((item) =>
      item.contacts.forEach((contact) => recipients.add(contact.id))
    );
    return {
      total: items.length,
      week: week.length,
      recipients: recipients.size,
    };
  }, [items]);

  if (!items.length) {
    return (
      <div className="ms-emptyIntro">
        <div className="ms-emptyIntro__eyebrow">
          <FontAwesomeIcon icon={["fas", "wand-magic-sparkles"] as any} />
          {intl.formatMessage({ id: "app.messageScheduling.empty.eyebrow" })}
        </div>
        <h4 className="ms-emptyIntro__title">
          {intl.formatMessage({ id: "app.messageScheduling.empty.title" })}
        </h4>
        <p className="ms-emptyIntro__text">
          {intl.formatMessage({ id: "app.messageScheduling.empty.text" })}
        </p>
        <ol className="ms-emptyIntro__steps">
          <li>
            {intl.formatMessage({ id: "app.messageScheduling.empty.step1" })}
          </li>
          <li>
            {intl.formatMessage({ id: "app.messageScheduling.empty.step2" })}
          </li>
          <li>
            {intl.formatMessage({ id: "app.messageScheduling.empty.step3" })}
          </li>
        </ol>
        <Button className="ms-btn ms-btn--primary align-self-start" onClick={onCreate}>
          <FontAwesomeIcon icon={["fas", "plus"]} className="mr-2" />
          {intl.formatMessage({ id: "app.messageScheduling.create" })}
        </Button>
      </div>
    );
  }

  return (
    <>
      <div className="ms-stats">
        <div className="ms-stat">
          <span className="ms-stat__value">{stats.total}</span>
          <span className="ms-stat__label">
            {intl.formatMessage({ id: "app.messageScheduling.stats.total" })}
          </span>
        </div>
        <div className="ms-stat">
          <span className="ms-stat__value">{stats.week}</span>
          <span className="ms-stat__label">
            {intl.formatMessage({ id: "app.messageScheduling.stats.week" })}
          </span>
        </div>
        <div className="ms-stat">
          <span className="ms-stat__value">{stats.recipients}</span>
          <span className="ms-stat__label">
            {intl.formatMessage({
              id: "app.messageScheduling.stats.recipients",
            })}
          </span>
        </div>
      </div>

      <div className="ms-list">
        {items.map((item) => {
          const isPast = moment(item.scheduledAt).isBefore(moment());
          return (
            <div
              key={item.id}
              className={`ms-card ms-card--${item.channel} ${
                isPast ? "is-past" : ""
              }`}
            >
              <div className="ms-card__channel">
                <FontAwesomeIcon
                  icon={["fab", CHANNEL_ICON[item.channel] as any]}
                />
              </div>

              <div className="ms-card__body">
                <div className="ms-card__badges">
                  <span className={`ms-badge ms-badge--${item.channel}`}>
                    {intl.formatMessage({
                      id: `app.messageScheduling.channel.${item.channel}`,
                    })}
                  </span>
                  <span
                    className={`ms-badge ms-badge--status ${
                      isPast ? "ms-badge--muted" : ""
                    }`}
                  >
                    {intl.formatMessage({
                      id: `app.messageScheduling.status.${item.status}`,
                    })}
                  </span>
                  <span className="ms-card__when">
                    <FontAwesomeIcon icon={["far", "clock"]} />
                    {moment(item.scheduledAt).format("DD/MM/YYYY HH:mm")}
                  </span>
                </div>

                <div className="ms-card__text">
                  {item.text ||
                    item.media.map((media) => media.name).join(", ") ||
                    (item.link?.url ? linkHostname(item.link.url) : "—")}
                </div>

                <div className="ms-card__meta">
                  <span>
                    <FontAwesomeIcon icon={["fas", "users"]} />
                    {intl.formatMessage(
                      { id: "app.messageScheduling.card.recipients" },
                      { count: item.contacts.length }
                    )}
                  </span>
                  {item.media.length > 0 && (
                    <span>
                      <FontAwesomeIcon icon={["fas", "paperclip"]} />
                      {item.media.length}
                    </span>
                  )}
                  {item.link?.url && (
                    <span>
                      <FontAwesomeIcon icon={["fas", "link"]} />
                      {linkHostname(item.link.url)}
                    </span>
                  )}
                </div>

                <div className="ms-card__recipients">
                  {item.contacts.slice(0, 5).map((contact) => (
                    <span key={contact.id} className="ms-chip ms-chip--sm">
                      {contact.name}
                    </span>
                  ))}
                  {item.contacts.length > 5 && (
                    <span className="ms-chip ms-chip--sm">
                      +{item.contacts.length - 5}
                    </span>
                  )}
                </div>
              </div>

              <div className="ms-card__actions">
                <button
                  type="button"
                  className="ms-iconBtn"
                  title={intl.formatMessage({
                    id: "app.messageScheduling.edit",
                  })}
                  onClick={() => onEdit(item)}
                >
                  <FontAwesomeIcon icon={["fas", "pen"]} />
                </button>
                <button
                  type="button"
                  className="ms-iconBtn"
                  title={intl.formatMessage({
                    id: "app.messageScheduling.duplicate",
                  })}
                  onClick={() => onDuplicate(item)}
                >
                  <FontAwesomeIcon icon={["far", "clone"]} />
                </button>
                <button
                  type="button"
                  className="ms-iconBtn ms-iconBtn--danger"
                  title={intl.formatMessage({
                    id: "app.messageScheduling.delete",
                  })}
                  onClick={() => onDelete(item.id)}
                >
                  <FontAwesomeIcon icon={["far", "trash-can"] as any} />
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
};
